import {
  Dialog,
  Button,
  Typography,
  Box,
  TextField,
  Snackbar,
  Checkbox,
  Select,
  MenuItem,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { ManagementUsersDialogStyle as styles } from "./ManagementUsersDialogStyle";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { pendingShipmentUpdatedFlagState } from "../recoil/atoms/pendingShipmentAtoms";
import { UserListItem } from "./Table/UserListTable";
import { baseSearchOptionsSelector } from "../recoil/selectors/baseSearchOptionsSelector";
import BaseFilterSelect from "./BaseFilter";
import { CheckBox } from "@mui/icons-material";
import SelectBox from "./common/SelectBox";
import WarehousesSettingDialog from "./WarehousesSettingDialog";

export const warehouseMap: Record<number, keyof UserListItem> = {
  1: "isHopeFsz1",
  2: "isHopeHnd2",
  3: "isHopeHnd3",
  4: "isHopeHnd6",
  5: "isHopeHnd9",
  6: "isHopeHsg1",
  7: "isHopeKix1",
  8: "isHopeKix2",
  9: "isHopeKix3",
  10: "isHopeKix4",
  11: "isHopeKix5",
  12: "isHopeKix6",
  13: "isHopeNrt1",
  14: "isHopeNrt2",
  15: "isHopeNrt5",
  16: "isHopeNgo2",
  17: "isHopeQcb1",
  18: "isHopeQcb3",
  19: "isHopeQcb4",
  20: "isHopeQcb5",
  21: "isHopeTpb1",
  22: "isHopeTpb2",
  23: "isHopeTpb3",
  24: "isHopeTpb5",
  25: "isHopeTpb9",
  26: "isHopeTpf2",
  27: "isHopeTpf3",
  28: "isHopeTpf4",
  29: "isHopeTpf6",
  30: "isHopeTpf9",
  31: "isHopeTpf_a",
  32: "isHopeTpf_b",
  33: "isHopeTpf_c",
  34: "isHopeTpf_d",
  35: "isHopeTpf_i",
  36: "isHopeTpn1",
  37: "isHopeTpn2",
  38: "isHopeTpn3",
  39: "isHopeTpx1",
  40: "isHopeTpx2",
  41: "isHopeTpy1",
  42: "isHopeTpy2",
  43: "isHopeTpy3",
  44: "isHopeTpy5",
  45: "isHopeTpz1",
  46: "isHopeTpz2",
  47: "isHopeTpz3",
  48: "isHopeTpz4",
  49: "isHopeTpz5",
  50: "isHopeTpz6",
  51: "isHopeTpz7",
  52: "isHopeTpz8",
  53: "isHopeTpz9",
  54: "isHopeTpr1",
  55: "isHopeTyo1",
  56: "isHopeTyo2",
  57: "isHopeTyo4",
  58: "isHopeTyo6",
  59: "isHopeTyo7",
  60: "isHopeTyo8",
  61: "isHopeTyo9",
  62: "isHopeVjg_t",
  63: "isHopeVjn_a",
  64: "isHopeVjn_b",
  65: "isHopeVjn_c",
  66: "isHopeVjn_d",
  67: "isHopeVjn_e",
  68: "isHopeVjw_a",
  69: "isHopeVjw_b",
  70: "isHopeXje1",
  71: "isHopeXje2",
};

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedUser: UserListItem, baseIds: number[]) => void;
  statusId: number;
  userInfo: UserListItem;
};

const ManagementUsersDialog: React.FC<Props> = ({
  open,
  userInfo,
  onClose,
  onUpdate,
}) => {
  const setPendingShipmentUpdateFlag = useSetRecoilState(
    pendingShipmentUpdatedFlagState
  );

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">(
    "success"
  );
  const handleCloseToast = () => setToastOpen(false);

  const [userNum, setUserNum] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstrName] = useState("");
  const [mailAddress, setMailAddress] = useState("");
  const [shopName, setShopName] = useState("");
  const [roomId, setRoomId] = useState("");
  const [driverName, setDriverName] = useState("");
  const [memo, setMemo] = useState("");
  const [isHopeWarehuse, setIsHopeWarehouse] = useState(
    userInfo?.isHopeWarehouse ?? false
  );
  const [editUserInfo, setEditUserInfo] = useState<UserListItem>();
  const [baseName, setBaseName] = useState("");
  const [selectedBaseId, setSelectedBaseId] = useState<number | null>(null);
  const [selectedBaseIds, setSelectedBaseIds] = useState<number[]>([]);

  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);

  // 拠点データ取得
  const baseSearchOptions = useRecoilValue(baseSearchOptionsSelector);
  const baseOptions = useMemo(() => {
    return (
      baseSearchOptions?.map((base) => ({
        label: `${base.name}`,
        id: base.id!,
      })) ?? []
    );
  }, [baseSearchOptions]);

  const resetFields = () => {
    if (userInfo) {
      setUserNum(String(userInfo.userNumber ?? "0"));
      setLastName(userInfo.lastName ?? "");
      setFirstrName(userInfo.firstName ?? "");
      setMailAddress(userInfo.mailAddress ?? "");
      setShopName(userInfo.shopName ?? "");
      setRoomId(String(userInfo.roomId ?? ""));
      setDriverName(userInfo.driverName ?? "");
      setMemo(userInfo.memo ?? "");

      // エラー対策: 空配列をデフォルトに
      const base = (baseSearchOptions ?? []).find(
        (b) => b.name === userInfo.base
      );
      setSelectedBaseId(base?.id ?? null);
      setBaseName(userInfo.base ?? "");
      setSelectedBaseIds(userInfo.baseIds ?? []);

      setEditUserInfo(userInfo);
    }
  };

  // 初期値セット
  useEffect(() => {
    resetFields();
    if (!userInfo) return;
    setSelectedBaseIds(userInfo.baseIds ?? []);
    setIsHopeWarehouse(userInfo.isHopeWarehouse ?? false);
  }, [userInfo]);

  // 入力欄データまとめ（拠点除く）
  const inputItems: [
    string,
    string,
    React.Dispatch<React.SetStateAction<string>>,
    keyof UserListItem
  ][] = [
    ["ユーザー番号", userNum, setUserNum, "userNumber"],
    ["性", lastName, setLastName, "lastName"],
    ["名", firstName, setFirstrName, "firstName"],
    ["メールアドレス", mailAddress, setMailAddress, "mailAddress"],
    ["ショップ名", shopName, setShopName, "shopName"],
    ["roomID", roomId, setRoomId, "roomId"],
    ["配送名", driverName, setDriverName, "driverName"],
    ["メモ", memo, setMemo, "memo"],
  ];

  const updateWarehouse = (
    updatedUser: UserListItem,
    warehouseIds: number[]
  ) => {
    const updatedFlags: Partial<UserListItem> = Object.fromEntries(
      Object.entries(warehouseMap).map(([id, key]) => [
        key,
        warehouseIds.includes(Number(id)) ? true : false,
      ])
    ) as Partial<UserListItem>;
    console.log(warehouseIds);
    console.log(updatedFlags);
    setEditUserInfo((prev) => ({
      ...prev!,
      ...updatedFlags,
    }));
    setWarehouseDialogOpen(false);
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
        <WarehousesSettingDialog
          open={warehouseDialogOpen}
          onClose={() => {
            setWarehouseDialogOpen(false);
          }}
          userInfo={userInfo}
          onUpdate={updateWarehouse}
        />

        <Dialog
          open={open}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") onClose();
          }}
          disableEnforceFocus
          maxWidth={false}
          fullWidth={false}
          PaperProps={{
            sx: {
              display: "flex",
              width: "700px",
              height: "700px",
              maxWidth: "none",
              maxHeight: "none",
              overflowY: "auto",
            },
          }}
        >
          <Box sx={styles.header}>
            <Typography sx={styles.headerText}>ユーザー情報更新</Typography>
            <Button
              onClick={() => {
                resetFields();
                onClose();
              }}
              sx={styles.closeButton}
            >
              ×
            </Button>
          </Box>

          <Box sx={styles.formArea}>
            {inputItems.slice(0, 1).map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (/^\d*$/.test(newValue)) {
                      setter(newValue);
                      setEditUserInfo((prev) => ({
                        ...prev!,
                        [key]: newValue,
                      }));
                      setter(newValue);
                      setEditUserInfo((prev) => ({
                        ...prev!,
                        [key]: newValue,
                      }));
                    }
                  }}
                  sx={{ width: "250px" }}
                />
              </Box>
            ))}
            {inputItems.slice(1, 4).map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setter(newValue);
                    setEditUserInfo((prev) => ({
                      ...prev!,
                      [key]: newValue,
                    }));
                  }}
                  sx={{ width: "250px" }}
                />
              </Box>
            ))}
            {/* 4️⃣ 拠点: BaseFilterSelect */}
            <Box sx={styles.inputRow}>
              <Typography sx={styles.label}>拠点：</Typography>
              <BaseFilterSelect
                options={baseOptions}
                values={selectedBaseIds}
                onSelectMultiple={(newBaseIds) => {
                  setSelectedBaseIds(newBaseIds);
                  const baseNames = baseOptions
                    .filter((b) => newBaseIds.includes(b.id))
                    .map((b) => b.label)
                    .join("・");
                  setBaseName(baseNames);
                  setEditUserInfo((prev) => ({
                    ...prev!,
                    base: baseNames,
                    baseId: newBaseIds[0],
                  }));
                  setSelectedBaseIds(newBaseIds);
                }}
                isMultiple={true}
                sx={{ width: "250px" }}
                labelFontSize="0.9rem"
                onSelect={function (id: number | null): void {
                  throw new Error("Function not implemented.");
                }}
              />
            </Box>
            {inputItems.slice(4).map(([label, value, setter, key]) => (
              <Box key={label} sx={styles.inputRow}>
                <Typography sx={styles.label}>{label}：</Typography>
                <TextField
                  size="medium"
                  value={value}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setter(newValue);
                    setEditUserInfo((prev) => ({
                      ...prev!,
                      [key]: newValue,
                    }));
                  }}
                  sx={{ width: "250px" }}
                />
              </Box>
            ))}
            <Box sx={styles.inputRow}>
              <Typography sx={styles.label}>希望倉庫：</Typography>
              <Select
                label="希望倉庫"
                value={isHopeWarehuse ? 1 : 0}
                onChange={(e) => {
                  const selected = e.target.value === 1 ? true : false;
                  setIsHopeWarehouse(selected);
                  setEditUserInfo((prev) => ({
                    ...prev!,
                    isHopeWarehouse: selected,
                  }));
                }}
                sx={{ width: "150px" }}
              >
                <MenuItem value={1}>指定する</MenuItem>
                <MenuItem value={0}>指定しない</MenuItem>
              </Select>
              <Button
                variant="contained"
                onClick={() => setWarehouseDialogOpen(true)}
                disabled={!isHopeWarehuse}
              >
                倉庫選択
              </Button>
            </Box>
          </Box>

          <Button
            onClick={() => {
              onUpdate(editUserInfo!, selectedBaseIds);
            }}
            variant="contained"
            sx={styles.floatingRegisterButton}
          >
            更新
          </Button>

          <Snackbar
            open={toastOpen}
            autoHideDuration={4000}
            onClose={handleCloseToast}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <MuiAlert
              onClose={handleCloseToast}
              severity={toastSeverity}
              sx={{ width: "100%" }}
              elevation={6}
              variant="filled"
            >
              {toastMessage}
            </MuiAlert>
          </Snackbar>
        </Dialog>
      </LocalizationProvider>
    </>
  );
};

export default ManagementUsersDialog;
